import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { can } from '../lib/perms';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import {
  LayoutDashboard,
  Package,
  Truck,
  ChefHat,
  ClipboardList,
  Settings,
  LogOut,
  Menu,
  X,
  ShoppingCart,
  UtensilsCrossed,
  BarChart3,
  Grid3x3,
  PanelLeftClose,
  PanelLeftOpen
} from 'lucide-react';

const COLLAPSE_KEY = 'sidebar_collapsed';

const BrandMark = ({ className = 'w-8 h-8' }) => (
  <img src="/favicon.png" alt="TMC" className={`${className} rounded-lg object-contain`} data-testid="brand-mark" />
);

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(localStorage.getItem(COLLAPSE_KEY) === '1');
  const toggleCollapsed = () => { localStorage.setItem(COLLAPSE_KEY, collapsed ? '0' : '1'); setCollapsed(!collapsed); };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Supplier users get a minimal nav
  const isSupplier = user?.role === 'supplier';

  const navItems = isSupplier
    ? [
        { path: '/supplier-dashboard', label: 'My Orders', icon: ShoppingCart },
      ]
    : [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, perm: 'dashboard.view' },
        { path: '/menu-products', label: 'Menu Products', icon: UtensilsCrossed, perm: 'menu.view' },
        { path: '/recipes', label: 'Recipes', icon: ChefHat, perm: 'menu.view' },
        { path: '/stock-items', label: 'Stock Items', icon: Package, perm: 'stock_items.view' },
        { path: '/par-grid', label: 'PAR Grid', icon: Grid3x3, perm: 'par_grid.view' },
        { path: '/stock-take', label: 'Stock Take', icon: ClipboardList, perm: 'stock_take.view' },
        { path: '/orders', label: 'Purchase Orders', icon: ShoppingCart, perm: 'orders.view' },
        { path: '/suppliers', label: 'Suppliers', icon: Truck, perm: 'suppliers.view' },
        { path: '/reports', label: 'Reports', icon: BarChart3, perm: 'reports.view' },
        { path: '/settings', label: 'Settings', icon: Settings },
      ].filter((i) => !i.perm || can(user, i.perm));

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path || 
                     (item.path === '/recipes' && location.pathname.startsWith('/recipes')) ||
                     (item.path === '/settings' && location.pathname.startsWith('/settings'));
    const Icon = item.icon;

    return (
      <Link
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        title={item.label}
        className={`sidebar-item ${isActive ? 'active' : ''} ${collapsed ? 'desk:justify-center desk:px-0' : ''}`}
        data-testid={`nav-${item.path.replace('/', '')}`}
      >
        <Icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
        <span className={`font-medium ${collapsed ? 'desk:hidden' : ''}`}>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="desk:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 flex items-center px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
        <BrandMark className="ml-2 w-8 h-8" />
        <h1 className="ml-3 text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
          TMC Stock App
        </h1>
        <div className="ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="desk:hidden fixed inset-0 bg-black/50 z-40"
          data-testid="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50 transform transition-[transform,width] duration-200 desk:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'desk:w-20' : ''}`}
        data-testid="sidebar"
      >
        <div className={`h-16 flex items-center pl-6 pr-3 border-b border-border ${collapsed ? 'desk:px-0 desk:justify-center' : ''}`}>
          <div className="flex items-center gap-3">
            <BrandMark />
            <span className={`text-lg font-bold whitespace-nowrap ${collapsed ? 'desk:hidden' : ''}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
              TMC Stock App
            </span>
          </div>
          <Button variant="ghost" size="icon" className="ml-auto desk:hidden" onClick={() => setSidebarOpen(false)} data-testid="close-sidebar-btn">
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className={`hidden desk:flex px-4 pt-3 ${collapsed ? 'justify-center px-0' : 'justify-end'}`}>
          <Button variant="ghost" size="icon" onClick={toggleCollapsed} title={collapsed ? 'Expand menu' : 'Collapse menu'} data-testid="collapse-sidebar-btn">
            {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </Button>
        </div>

        <ScrollArea className={collapsed ? 'h-[calc(100vh-8rem)] desk:h-[calc(100vh-14rem)]' : 'h-[calc(100vh-8rem)] desk:h-[calc(100vh-11rem)]'}>
          <nav className={`p-4 space-y-1 ${collapsed ? 'desk:px-3' : ''}`}>
            {navItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </nav>
        </ScrollArea>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-card">
          <div className={`flex items-center gap-3 ${collapsed ? 'desk:flex-col desk:gap-2' : ''}`}>
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className={`flex-1 min-w-0 ${collapsed ? 'desk:hidden' : ''}`}>
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`pt-16 desk:pt-0 transition-[padding] duration-200 ${collapsed ? 'desk:pl-20' : 'desk:pl-64'}`}>
        <div className="container mx-auto px-4 md:px-8 py-8 max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
